@if (loading()) {
  <div class="flex items-center gap-3 p-6 text-sm text-muted-foreground">
    <hlm-spinner size="20" /> Loading notifications & forwardings...
  </div>
}

@if (error(); as err) {
  <div class="mx-auto max-w-[1400px]">
    <cmt-error-state
      title="Failed to load notifications & forwardings"
      [message]="err"
      (retry)="loadAll()" />
  </div>
}

@if (!loading() && !error()) {
  <div class="mx-auto flex max-w-[1400px] flex-col gap-6">

    <!-- Hero card — sticky under the 50px titlebar so filter + Collapse-all stay reachable -->
    <div class="sticky top-[50px] z-20 rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div class="flex min-w-0 flex-col gap-1">
          <p class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">
            Phone Service
          </p>
          <h1 class="font-display text-2xl font-semibold leading-tight text-foreground">
            Notifications &amp; Forwardings
          </h1>
        </div>
        <div class="flex flex-none items-center gap-2 pt-1">
          <button hlmBtn variant="outline" (click)="toggleAll()">
            <hlm-icon name="lucideChevronsUpDown" size="0.9rem" />
            {{ allCollapsed() ? 'Expand all' : 'Collapse all' }}
          </button>
        </div>
      </div>

      <!-- Filter bar -->
      <div class="relative mt-4">
        <hlm-icon name="lucideSearch" size="0.9rem" class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input hlmInput class="pl-9 pr-9" placeholder="Filter by name, destination or type..."
          [value]="filterText()"
          (input)="filterText.set($any($event.target).value)" />
        @if (filterText()) {
          <button hlmBtn variant="ghost" size="icon" class="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
            (click)="filterText.set('')" hlmTooltip="Clear">
            <hlm-icon name="lucideX" size="0.8rem" />
          </button>
        }
      </div>

      <!-- Type quick-filter — PUSH is deselected by default (it's the noisy one) -->
      @if (availableTypes().length > 1) {
        <div class="mt-3">
          <cmt-type-filter
            [types]="availableTypes()"
            [selected]="effectiveSelectedTypes()"
            (selectedChange)="selectedTypes.set($event)" />
        </div>
      }
    </div>

    @if (hiddenSectionCount() > 0) {
      <div class="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
        <hlm-icon name="lucideFilter" size="0.9rem" />
        {{ hiddenSectionCount() }} section{{ hiddenSectionCount() > 1 ? 's' : '' }} with no matching rows
      </div>
    }

    <!-- ========== S1 Call Notifications ========== -->
    @if (sectionHasResults().callNotif) {
    <section [cmtRevealOnExpand]="!isCollapsed('callNotif')" class="rounded-2xl border border-border bg-card shadow-sm">
      <header class="flex cursor-pointer items-center gap-3 px-5 py-4 transition-colors hover:bg-muted/30"
        (click)="toggleSection('callNotif')" [attr.aria-expanded]="!isCollapsed('callNotif')">
        <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
          <hlm-icon name="lucideBell" size="1rem" />
        </span>
        <h2 class="font-display text-lg font-semibold text-foreground">Call Notifications</h2>
        @if (reducedCounts().callNotif; as shownOf) {
          <span class="font-mono-feat text-sm text-muted-foreground">{{ shownOf }}</span>
        }
        <span class="flex-1"></span>
        @if (callNotifications().length > 0) {
          <button hlmBtn variant="outline" size="sm" class="h-8 border-destructive/40 text-destructive hover:bg-destructive/10 hover:border-destructive" (click)="$event.stopPropagation(); deleteAllInSection('callNotif')">
            <hlm-icon name="lucideTrash2" size="0.85rem" /> Delete all
          </button>
        }
        <button hlmBtn variant="outline" size="sm" class="h-8" (click)="$event.stopPropagation(); openAddDialog('callNotif', 'Call Notifications')">
          <hlm-icon name="lucidePlus" size="0.85rem" /> Add
        </button>
        <hlm-icon [name]="isCollapsed('callNotif') ? 'lucideChevronDown' : 'lucideChevronUp'" size="1rem" class="text-muted-foreground" />
      </header>

      @if (!isCollapsed('callNotif')) {
        <div class="overflow-x-auto border-t border-border px-5 py-4">
          @if (filteredCallNotifications().length > 0) {
            <div class="mb-1 hidden grid-cols-[minmax(140px,1.2fr)_minmax(120px,2fr)_120px_110px] items-center gap-3 border-b border-border/70 px-2 pb-2 font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground md:grid">
              <span>Name</span><span>Destination</span><span>Type</span><span></span>
            </div>
            <cmt-capped-list [count]="filteredCallNotifications().length">
            <div class="flex flex-col divide-y divide-border/70">
              @for (n of filteredCallNotifications(); track n.notificationId) {
                <div class="grid grid-cols-[minmax(140px,1.2fr)_minmax(120px,2fr)_120px_110px] items-center gap-3 px-2 py-2.5">
                  <span class="min-w-0 truncate text-sm font-medium text-foreground">{{ n.reachability.name }}</span>
                  <span class="flex min-w-0 items-center gap-1">
                    <span class="min-w-0 truncate font-mono-feat text-sm text-muted-foreground" [title]="isTokenTruncated(n.reachability.destination) ? n.reachability.destination : null">{{ n.reachability.destination | truncateToken }}</span>
                    <button hlmBtn variant="ghost" size="icon" class="h-7 w-7 flex-none" (click)="copyToClipboard(n.reachability.destination, $event)" hlmTooltip="Copy">
                      <hlm-icon name="lucideCopy" size="0.8rem" />
                    </button>
                  </span>
                  <span><hlm-badge [variant]="typeBadgeVariant(n.reachability.type)">{{ n.reachability.type }}</hlm-badge></span>
                  <span class="flex items-center justify-end">
                    <button hlmBtn variant="destructive" size="sm" class="h-7 text-sm hover:bg-destructive/85 hover:shadow-md" (click)="deleteNotification(n, 'callNotif')">
                      <hlm-icon name="lucideTrash2" size="0.75rem" /> Delete
                    </button>
                  </span>
                </div>
              }
            </div>
            </cmt-capped-list>
          } @else if (callNotifications().length > 0) {
            <p class="px-2 py-3 text-sm text-muted-foreground">No call notifications match the active filters.</p>
          } @else {
            <div class="rounded-2xl border border-dashed border-border bg-card/50 p-8 text-center text-sm text-muted-foreground">
              No call notifications configured
            </div>
          }
        </div>
      }
    </section>
    }

    <!-- ========== S2 Manual Forwardings ========== -->
    @if (sectionHasResults().manual) {
    <section [cmtRevealOnExpand]="!isCollapsed('manual')" class="rounded-2xl border border-border bg-card shadow-sm">
      <header class="flex cursor-pointer items-center gap-3 px-5 py-4 transition-colors hover:bg-muted/30"
        (click)="toggleSection('manual')" [attr.aria-expanded]="!isCollapsed('manual')">
        <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
          <hlm-icon name="lucidePhoneForwarded" size="1rem" />
        </span>
        <h2 class="font-display text-lg font-semibold text-foreground">Manual Forwardings</h2>
        @if (reducedCounts().manual; as shownOf) {
          <span class="font-mono-feat text-sm text-muted-foreground">{{ shownOf }}</span>
        }
        <span class="flex-1"></span>
        @if (manualForwardings().length > 0) {
          <button hlmBtn variant="outline" size="sm" class="h-8 border-destructive/40 text-destructive hover:bg-destructive/10 hover:border-destructive" (click)="$event.stopPropagation(); deleteAllInSection('manual')" [disabled]="reordering()">
            <hlm-icon name="lucideTrash2" size="0.85rem" /> Delete all
          </button>
        }
        <button hlmBtn variant="outline" size="sm" class="h-8" (click)="$event.stopPropagation(); openAddDialog('manual', 'Manual Forwardings')">
          <hlm-icon name="lucidePlus" size="0.85rem" /> Add
        </button>
        <hlm-icon [name]="isCollapsed('manual') ? 'lucideChevronDown' : 'lucideChevronUp'" size="1rem" class="text-muted-foreground" />
      </header>

      @if (!isCollapsed('manual')) {
        <div class="overflow-x-auto border-t border-border px-5 py-4">
          @if (manualReorderFiltered()) {
            <div class="mb-3 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2" data-testid="manual-reorder-filter-warning">
              <hlm-icon name="lucideTriangleAlert" size="0.9rem" class="mt-0.5 flex-none text-warning" />
              <p class="text-sm text-foreground">
                Filter is active — {{ manualForwardings().length - filteredManualForwardings().length }} of {{ manualForwardings().length }} forwardings are hidden. Moving swaps with the next visible entry; hidden entries keep their position.
              </p>
            </div>
          }
          @if (filteredManualForwardings().length > 0) {
            <div class="mb-1 hidden grid-cols-[2rem_minmax(140px,1.2fr)_minmax(120px,2fr)_120px_190px] items-center gap-3 border-b border-border/70 px-2 pb-2 font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground md:grid">
              <span>#</span><span>Name</span><span>Destination</span><span>Type</span><span></span>
            </div>
            <cmt-capped-list [count]="filteredManualForwardings().length">
            <div class="flex flex-col divide-y divide-border/70">
              @for (f of filteredManualForwardings(); track f.forwardingId; let i = $index) {
                <div class="grid grid-cols-[2rem_minmax(140px,1.2fr)_minmax(120px,2fr)_120px_190px] items-center gap-3 px-2 py-2.5">
                  <span class="font-mono-feat text-sm text-muted-foreground">{{ i + 1 }}</span>
                  <span class="min-w-0 truncate text-sm font-medium text-foreground">{{ f.reachability.name }}</span>
                  <span class="flex min-w-0 items-center gap-1">
                    <span class="min-w-0 truncate font-mono-feat text-sm text-muted-foreground" [title]="isTokenTruncated(f.reachability.destination) ? f.reachability.destination : null">{{ f.reachability.destination | truncateToken }}</span>
                    <button hlmBtn variant="ghost" size="icon" class="h-7 w-7 flex-none" (click)="copyToClipboard(f.reachability.destination, $event)" hlmTooltip="Copy">
                      <hlm-icon name="lucideCopy" size="0.8rem" />
                    </button>
                  </span>
                  <span><hlm-badge [variant]="typeBadgeVariant(f.reachability.type)">{{ f.reachability.type }}</hlm-badge></span>
                  <span class="flex items-center justify-end gap-1">
                    <button hlmBtn variant="outline" size="icon" class="h-8 w-8" (click)="moveUp(f, filteredManualForwardings(), 'manual')" [disabled]="i === 0 || reordering()" hlmTooltip="Move up" aria-label="Move up">
                      <hlm-icon name="lucideChevronUp" size="0.9rem" />
                    </button>
                    <button hlmBtn variant="outline" size="icon" class="h-8 w-8" (click)="moveDown(f, filteredManualForwardings(), 'manual')" [disabled]="i === filteredManualForwardings().length - 1 || reordering()" hlmTooltip="Move down" aria-label="Move down">
                      <hlm-icon name="lucideChevronDown" size="0.9rem" />
                    </button>
                    <span class="w-3"></span>
                    <button hlmBtn variant="destructive" size="sm" class="h-7 text-sm hover:bg-destructive/85 hover:shadow-md" (click)="deleteForwarding(f, 'manual')" [disabled]="reordering()">
                      <hlm-icon name="lucideTrash2" size="0.75rem" /> Delete
                    </button>
                  </span>
                </div>
              }
            </div>
            </cmt-capped-list>
          } @else if (manualForwardings().length > 0) {
            <p class="px-2 py-3 text-sm text-muted-foreground">No manual forwardings match the active filters.</p>
          } @else {
            <div class="rounded-2xl border border-dashed border-border bg-card/50 p-8 text-center text-sm text-muted-foreground">
              No manual forwardings configured
            </div>
          }
        </div>
      }
    </section>
    }

    <!-- ========== S3 Busy Forwardings ========== -->
    @if (sectionHasResults().busy) {
    <section [cmtRevealOnExpand]="!isCollapsed('busy')" class="rounded-2xl border border-border bg-card shadow-sm">
      <header class="flex cursor-pointer items-center gap-3 px-5 py-4 transition-colors hover:bg-muted/30"
        (click)="toggleSection('busy')" [attr.aria-expanded]="!isCollapsed('busy')">
        <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
          <hlm-icon name="lucidePhoneOff" size="1rem" />
        </span>
        <h2 class="font-display text-lg font-semibold text-foreground">Busy Forwardings</h2>
        @if (reducedCounts().busy; as shownOf) {
          <span class="font-mono-feat text-sm text-muted-foreground">{{ shownOf }}</span>
        }
        <span class="flex-1"></span>
        @if (busyForwardings().length > 0) {
          <button hlmBtn variant="outline" size="sm" class="h-8 border-destructive/40 text-destructive hover:bg-destructive/10 hover:border-destructive" (click)="$event.stopPropagation(); deleteAllInSection('busy')" [disabled]="reordering()">
            <hlm-icon name="lucideTrash2" size="0.85rem" /> Delete all
          </button>
        }
        <button hlmBtn variant="outline" size="sm" class="h-8" (click)="$event.stopPropagation(); openAddDialog('busy', 'Busy Forwardings')">
          <hlm-icon name="lucidePlus" size="0.85rem" /> Add
        </button>
        <hlm-icon [name]="isCollapsed('busy') ? 'lucideChevronDown' : 'lucideChevronUp'" size="1rem" class="text-muted-foreground" />
      </header>

      @if (!isCollapsed('busy')) {
        <div class="overflow-x-auto border-t border-border px-5 py-4">
          @if (busyReorderFiltered()) {
            <div class="mb-3 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2" data-testid="busy-reorder-filter-warning">
              <hlm-icon name="lucideTriangleAlert" size="0.9rem" class="mt-0.5 flex-none text-warning" />
              <p class="text-sm text-foreground">
                Filter is active — {{ busyForwardings().length - filteredBusyForwardings().length }} of {{ busyForwardings().length }} forwardings are hidden. Moving swaps with the next visible entry; hidden entries keep their position.
              </p>
            </div>
          }
          @if (filteredBusyForwardings().length > 0) {
            <div class="mb-1 hidden grid-cols-[2rem_minmax(120px,1fr)_minmax(120px,1.8fr)_minmax(140px,1.2fr)_220px] items-center gap-3 border-b border-border/70 px-2 pb-2 font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground md:grid">
              <span>#</span><span>Name</span><span>Destination</span><span>Schedule</span><span></span>
            </div>
            <cmt-capped-list [count]="filteredBusyForwardings().length">
            <div class="flex flex-col divide-y divide-border/70">
              @for (f of filteredBusyForwardings(); track f.forwardingId; let i = $index) {
                <div class="grid grid-cols-[2rem_minmax(120px,1fr)_minmax(120px,1.8fr)_minmax(140px,1.2fr)_220px] items-center gap-3 px-2 py-2.5">
                  <span class="font-mono-feat text-sm text-muted-foreground">{{ i + 1 }}</span>
                  <span class="min-w-0 truncate text-sm font-medium text-foreground">{{ f.reachability.name }}</span>
                  <span class="flex min-w-0 items-center gap-1">
                    <span class="min-w-0 truncate font-mono-feat text-sm text-muted-foreground" [title]="isTokenTruncated(f.reachability.destination) ? f.reachability.destination : null">{{ f.reachability.destination | truncateToken }}</span>
                    <button hlmBtn variant="ghost" size="icon" class="h-7 w-7 flex-none" (click)="copyToClipboard(f.reachability.destination, $event)" hlmTooltip="Copy">
                      <hlm-icon name="lucideCopy" size="0.8rem" />
                    </button>
                  </span>
                  <span class="min-w-0 truncate text-sm text-muted-foreground">{{ formatSchedule(f) }}</span>
                  <span class="flex items-center justify-end gap-1">
                    <button hlmBtn variant="outline" size="icon" class="h-8 w-8" (click)="moveUp(f, filteredBusyForwardings(), 'busy')" [disabled]="i === 0 || reordering()" hlmTooltip="Move up" aria-label="Move up">
                      <hlm-icon name="lucideChevronUp" size="0.9rem" />
                    </button>
                    <button hlmBtn variant="outline" size="icon" class="h-8 w-8" (click)="moveDown(f, filteredBusyForwardings(), 'busy')" [disabled]="i === filteredBusyForwardings().length - 1 || reordering()" hlmTooltip="Move down" aria-label="Move down">
                      <hlm-icon name="lucideChevronDown" size="0.9rem" />
                    </button>
                    <span class="w-3"></span>
                    <button hlmBtn variant="outline" size="sm" class="h-7 text-sm hover:bg-primary/10 hover:border-primary hover:text-primary" (click)="editSchedule(f, 'busy')" hlmTooltip="Edit schedule">
                      <hlm-icon name="lucidePencil" size="0.75rem" /> Edit
                    </button>
                    <button hlmBtn variant="destructive" size="sm" class="h-7 text-sm hover:bg-destructive/85 hover:shadow-md" (click)="deleteForwarding(f, 'busy')" [disabled]="reordering()">
                      <hlm-icon name="lucideTrash2" size="0.75rem" /> Delete
                    </button>
                  </span>
                </div>
              }
            </div>
            </cmt-capped-list>
          } @else if (busyForwardings().length > 0) {
            <p class="px-2 py-3 text-sm text-muted-foreground">No busy forwardings match the active filters.</p>
          } @else {
            <div class="rounded-2xl border border-dashed border-border bg-card/50 p-8 text-center text-sm text-muted-foreground">
              No busy forwardings configured
            </div>
          }
        </div>
      }
    </section>
    }

    <!-- ========== S4 Direct Forwardings (per number) ========== -->
    @if (sectionHasResults().direct) {
    <section [cmtRevealOnExpand]="!isCollapsed('direct')" class="rounded-2xl border border-border bg-card shadow-sm">
      <header class="flex cursor-pointer items-center gap-3 px-5 py-4 transition-colors hover:bg-muted/30"
        (click)="toggleSection('direct')" [attr.aria-expanded]="!isCollapsed('direct')">
        <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
          <hlm-icon name="lucideArrowUpRight" size="1rem" />
        </span>
        <h2 class="font-display text-lg font-semibold text-foreground">Direct Forwardings</h2>
        @if (reducedCounts().direct; as shownOf) {
          <span class="font-mono-feat text-sm text-muted-foreground">{{ shownOf }}</span>
        }
        <span class="flex-1"></span>
        <hlm-icon [name]="isCollapsed('direct') ? 'lucideChevronDown' : 'lucideChevronUp'" size="1rem" class="text-muted-foreground" />
      </header>

      @if (!isCollapsed('direct')) {
        <div class="border-t border-border">
          @if (directForwardingNumbers().length === 0) {
            <div class="px-5 py-4">
              <div class="rounded-2xl border border-dashed border-border bg-card/50 p-8 text-center text-sm text-muted-foreground">
                No numbers available
              </div>
            </div>
          }

          @for (group of filteredDirectGroups(); track group.number) {
            <div class="overflow-x-auto border-b border-border/50 px-5 py-4 last:border-b-0">
              <!-- Group header — number-specific Add stays here (per the number) -->
              <div class="mb-3 flex items-center gap-2">
                <hlm-icon name="lucidePhone" size="0.9rem" class="text-primary" />
                <span class="font-mono-feat text-sm font-medium text-foreground">{{ group.number }}</span>
                <hlm-badge [variant]="typeBadgeVariant(group.type)">{{ group.type }}</hlm-badge>
                @if (group.tags.length > 0) {
                  <span class="flex min-w-0 flex-wrap items-center gap-1">
                    @for (t of group.tags; track t.id) {
                      <hlm-badge variant="tag" class="gap-1"><hlm-icon name="lucideTag" size="0.75rem" />{{ t.name }}</hlm-badge>
                    }
                  </span>
                }
                @if (shownOf(group.forwardings.length, group.totalCount); as groupShownOf) {
                  <span class="font-mono-feat text-sm text-muted-foreground">{{ groupShownOf }}</span>
                }
                <span class="flex-1"></span>
                @if (group.forwardings.length > 0) {
                  <button hlmBtn variant="outline" size="sm" class="h-8 border-destructive/40 text-destructive hover:bg-destructive/10 hover:border-destructive" (click)="deleteAllDirect(group)">
                    <hlm-icon name="lucideTrash2" size="0.85rem" /> Delete all
                  </button>
                }
                <button hlmBtn variant="outline" size="sm" class="h-8" (click)="openAddDialog('direct', 'Direct Forwardings — ' + group.number, group.number)">
                  <hlm-icon name="lucidePlus" size="0.85rem" /> Add
                </button>
              </div>

              @if (group.forwardings.length > 0) {
                <div class="mb-1 hidden grid-cols-[2rem_minmax(120px,1fr)_minmax(120px,1.6fr)_100px_minmax(140px,1.2fr)_160px] items-center gap-3 border-b border-border/70 px-2 pb-2 font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground md:grid">
                  <span>#</span><span>Name</span><span>Destination</span><span>Type</span><span>Schedule</span><span></span>
                </div>
                <div class="flex flex-col divide-y divide-border/70">
                  @for (f of group.forwardings; track f.forwardingId; let i = $index) {
                    <div class="grid grid-cols-[2rem_minmax(120px,1fr)_minmax(120px,1.6fr)_100px_minmax(140px,1.2fr)_160px] items-center gap-3 px-2 py-2.5">
                      <span class="font-mono-feat text-sm text-muted-foreground">{{ i + 1 }}</span>
                      <span class="min-w-0 truncate text-sm font-medium text-foreground">{{ f.reachability.name }}</span>
                      <span class="flex min-w-0 items-center gap-1">
                        <span class="min-w-0 truncate font-mono-feat text-sm text-muted-foreground" [title]="isTokenTruncated(f.reachability.destination) ? f.reachability.destination : null">{{ f.reachability.destination | truncateToken }}</span>
                        <button hlmBtn variant="ghost" size="icon" class="h-7 w-7 flex-none" (click)="copyToClipboard(f.reachability.destination, $event)" hlmTooltip="Copy">
                          <hlm-icon name="lucideCopy" size="0.8rem" />
                        </button>
                      </span>
                      <span><hlm-badge [variant]="typeBadgeVariant(f.reachability.type)">{{ f.reachability.type }}</hlm-badge></span>
                      <span class="min-w-0 truncate text-sm text-muted-foreground">{{ formatSchedule(f) }}</span>
                      <span class="flex items-center justify-end gap-1">
                        <button hlmBtn variant="outline" size="sm" class="h-7 text-sm hover:bg-primary/10 hover:border-primary hover:text-primary" (click)="editSchedule(f, 'direct', group.number)" hlmTooltip="Edit schedule">
                          <hlm-icon name="lucidePencil" size="0.75rem" /> Edit
                        </button>
                        <button hlmBtn variant="destructive" size="sm" class="h-7 text-sm hover:bg-destructive/85 hover:shadow-md" (click)="deleteDirectForwarding(f, group.number)">
                          <hlm-icon name="lucideTrash2" size="0.75rem" /> Delete
                        </button>
                      </span>
                    </div>
                  }
                </div>
              } @else {
                <div class="rounded-xl border border-dashed border-border bg-card/50 p-6 text-center text-sm text-muted-foreground">
                  No direct forwardings for this number
                </div>
              }
            </div>
          }
        </div>
      }
    </section>
    }

    <!-- ========== S5 Answering Machine Notifications ========== -->
    @if (sectionHasResults().am) {
    <section [cmtRevealOnExpand]="!isCollapsed('am')" class="rounded-2xl border border-border bg-card shadow-sm">
      <header class="flex cursor-pointer items-center gap-3 px-5 py-4 transition-colors hover:bg-muted/30"
        (click)="toggleSection('am')" [attr.aria-expanded]="!isCollapsed('am')">
        <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
          <hlm-icon name="lucideMessageSquare" size="1rem" />
        </span>
        <h2 class="font-display text-lg font-semibold text-foreground">Answering Machine Notifications</h2>
        @if (reducedCounts().am; as shownOf) {
          <span class="font-mono-feat text-sm text-muted-foreground">{{ shownOf }}</span>
        }
        <span class="flex-1"></span>
        @if (amActive() && amNotifications().length > 0) {
          <button hlmBtn variant="outline" size="sm" class="h-8 border-destructive/40 text-destructive hover:bg-destructive/10 hover:border-destructive" (click)="$event.stopPropagation(); deleteAllInSection('am')">
            <hlm-icon name="lucideTrash2" size="0.85rem" /> Delete all
          </button>
        }
        @if (amActive()) {
          <button hlmBtn variant="outline" size="sm" class="h-8" (click)="$event.stopPropagation(); openAddDialog('am', 'AM Notifications')">
            <hlm-icon name="lucidePlus" size="0.85rem" /> Add
          </button>
        }
        <hlm-icon [name]="isCollapsed('am') ? 'lucideChevronDown' : 'lucideChevronUp'" size="1rem" class="text-muted-foreground" />
      </header>

      @if (!isCollapsed('am')) {
        <div class="overflow-x-auto border-t border-border px-5 py-4">
          @if (!amActive()) {
            <div class="rounded-2xl border border-dashed border-border bg-card/50 p-8 text-center text-sm text-muted-foreground">
              No answering machine active for this phone service
            </div>
          } @else if (filteredAmNotifications().length > 0) {
            <div class="mb-1 hidden grid-cols-[minmax(140px,1.2fr)_minmax(120px,2fr)_120px_110px] items-center gap-3 border-b border-border/70 px-2 pb-2 font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground md:grid">
              <span>Name</span><span>Destination</span><span>Type</span><span></span>
            </div>
            <cmt-capped-list [count]="filteredAmNotifications().length">
            <div class="flex flex-col divide-y divide-border/70">
              @for (n of filteredAmNotifications(); track n.notificationId) {
                <div class="grid grid-cols-[minmax(140px,1.2fr)_minmax(120px,2fr)_120px_110px] items-center gap-3 px-2 py-2.5">
                  <span class="min-w-0 truncate text-sm font-medium text-foreground">{{ n.reachability.name }}</span>
                  <span class="flex min-w-0 items-center gap-1">
                    <span class="min-w-0 truncate font-mono-feat text-sm text-muted-foreground" [title]="isTokenTruncated(n.reachability.destination) ? n.reachability.destination : null">{{ n.reachability.destination | truncateToken }}</span>
                    <button hlmBtn variant="ghost" size="icon" class="h-7 w-7 flex-none" (click)="copyToClipboard(n.reachability.destination, $event)" hlmTooltip="Copy">
                      <hlm-icon name="lucideCopy" size="0.8rem" />
                    </button>
                  </span>
                  <span><hlm-badge [variant]="typeBadgeVariant(n.reachability.type)">{{ n.reachability.type }}</hlm-badge></span>
                  <span class="flex items-center justify-end">
                    <button hlmBtn variant="destructive" size="sm" class="h-7 text-sm hover:bg-destructive/85 hover:shadow-md" (click)="deleteNotification(n, 'am')">
                      <hlm-icon name="lucideTrash2" size="0.75rem" /> Delete
                    </button>
                  </span>
                </div>
              }
            </div>
            </cmt-capped-list>
          } @else if (amNotifications().length > 0) {
            <p class="px-2 py-3 text-sm text-muted-foreground">No AM notifications match the active filters.</p>
          } @else {
            <div class="rounded-2xl border border-dashed border-border bg-card/50 p-8 text-center text-sm text-muted-foreground">
              No AM notifications configured
            </div>
          }
        </div>
      }
    </section>
    }

  </div>
}
